type Council = {
	displayName: string;
	postcodes: string[];
};

/**
 * The exact `administrative_area_level_2` value Google returns for addresses in this council. It is
 * match data, not a label, so it often differs from the council's `displayName` and must not be
 * renamed to match it. Cross-check new entries against the Google-sourced names in the
 * `065-MigrateCouncils` migration.
 */
type GoogleMapsCouncilMatch = string;

type State = Record<GoogleMapsCouncilMatch, Council>;

type StateCouncils = Record<string, State>;

const councilCodes: StateCouncils = {
	NSW: {
		'Northern Beaches Council': {
			displayName: 'Northern Beaches Council',
			postcodes: [
				'2084',
				'2085',
				'2086',
				'2087',
				'2092',
				'2093',
				'2094',
				'2095',
				'2096',
				'2097',
				'2099',
				'2100',
				'2101',
				'2102',
				'2103',
				'2104',
				'2105',
				'2106',
				'2107',
				'2108',
			],
		},
		'Central Coast Council': {
			displayName: 'Central Coast Council',
			postcodes: [
				'2083',
				'2257',
				'2261',
				'2250',
				'2258',
				'2262',
				'2251',
				'2259',
				'2263',
				'2256',
				'2260',
				'2775',
			],
		},
		'Port Stephens Council': {
			displayName: 'Port Stephens Council',
			postcodes: ['2314', '2295', '2315', '2316', '2317', '2318', '2319', '2322', '2324'],
		},
		'Lake Macquarie City Council': {
			displayName: 'Lake Macquarie Council',
			postcodes: [
				'2259',
				'2264',
				'2265',
				'2267',
				'2278',
				'2280',
				'2281',
				'2282',
				'2283',
				'2284',
				'2285',
				'2286',
				'2287',
				'2289',
				'2290',
				'2291',
				'2293',
				'2305',
				'2306',
				'2323',
			],
		},
		'Newcastle City Council': {
			displayName: 'Newcastle City Council',
			postcodes: [
				'2287',
				'2289',
				'2291',
				'2292',
				'2293',
				'2294',
				'2295',
				'2298',
				'2299',
				'2300',
				'2302',
				'2303',
				'2304',
				'2305',
				'2307',
				'2308',
				'2322',
			],
		},
		'Tamworth Regional Council': {
			displayName: 'Tamworth Regional Council',
			postcodes: ['2340', '2347', '2342', '2343', '2344', '2345', '2346', '2352', '2353', '2355'],
		},
	},
	SA: {
		'City of Adelaide': {
			displayName: 'City of Adelaide Council',
			postcodes: [
				'5000',
				'5005',
				'5006',
				'5007',
				'5031',
				'5034',
				'5035',
				'5061',
				'5063',
				'5065',
				'5067',
				'5069',
				'5081',
				'5082',
			],
		},
		'City of Charles Sturt': {
			displayName: 'City of Charles Sturt',
			postcodes: [
				'5007',
				'5008',
				'5009',
				'5011',
				'5012',
				'5013',
				'5014',
				'5019',
				'5020',
				'5021',
				'5022',
				'5023',
				'5024',
				'5025',
				'5082',
			],
		},
		'City of Port Adelaide Enfield': {
			displayName: 'Port Adelaide Enfield Council',
			postcodes: [
				'5008',
				'5009',
				'5010',
				'5012',
				'5013',
				'5014',
				'5015',
				'5016',
				'5017',
				'5018',
				'5019',
				'5021',
				'5070',
				'5074',
				'5075',
				'5081',
				'5082',
				'5083',
				'5084',
				'5085',
				'5086',
				'5087',
				'5088',
				'5093',
				'5094',
				'5095',
				'5098',
			],
		},
	},
	VIC: {
		'City of Ballarat': {
			displayName: 'City of Ballarat Council',
			postcodes: [
				'3350',
				'3351',
				'3352',
				'3353',
				'3354',
				'3355',
				'3356',
				'3357',
				'3358',
				'3363',
				'3364',
			],
		},
		'Warrnambool City': {
			displayName: 'City of Warrnambool',
			postcodes: ['3277', '3280', '3281', '3282', '3283'],
		},
		'Macedon Ranges Shire': {
			displayName: 'Macedon Ranges Shire Council',
			postcodes: [
				'3431',
				'3432',
				'3433',
				'3434',
				'3435',
				'3437',
				'3438',
				'3440',
				'3441',
				'3442',
				'3444',
				'3446',
				'3447',
			],
		},
		'Moyne Shire': {
			displayName: 'Shire of Moyne',
			postcodes: [
				'3265',
				'3268',
				'3272',
				'3273',
				'3274',
				'3275',
				'3276',
				'3277',
				'3278',
				'3279',
				'3281',
				'3282',
				'3283',
				'3284',
				'3285',
				'3286',
				'3287',
				'3289',
				'3301',
				'3304',
			],
		},
		'Yarra City': {
			displayName: 'Yarra City Council',
			postcodes: ['3054', '3065', '3066', '3067', '3068', '3078', '3121'],
		},
	},
};

const postcodesToCouncilName: Record<string, string> = {};

Object.values(councilCodes).forEach((stateCouncils) => {
	Object.values(stateCouncils).forEach((council) => {
		council.postcodes.forEach((councilCode) => {
			postcodesToCouncilName[councilCode] = council.displayName;
		});
	});
});

const normaliseCouncilName = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');

export const findCouncilByNameAndState = (
	councilName: string,
	state: string
): string | undefined => {
	const stateDetails = councilCodes[state];

	if (stateDetails) {
		const target = normaliseCouncilName(councilName);
		const stateCouncils = Object.entries(stateDetails);
		// Google is inconsistent about whether it suffixes "Council" onto an LGA name, so match
		// against both the Google name we expect and the name we display.
		const matchedCouncil = stateCouncils.find(
			([councilMatchName, council]) =>
				normaliseCouncilName(councilMatchName) === target ||
				normaliseCouncilName(council.displayName) === target
		);
		return matchedCouncil?.[1].displayName;
	}
};

export const findCouncilByPostcode = (postcode: string): string | undefined => {
	return postcodesToCouncilName[postcode];
};

export const findCouncil = (
	councilName: string | null | undefined,
	state: string | null | undefined,
	postcode: string | null | undefined
) => {
	if (councilName && state) {
		return findCouncilByNameAndState(councilName, state);
	} else if (postcode) {
		return findCouncilByPostcode(postcode);
	} else {
		return undefined;
	}
};
